(() => {
  "use strict";

  const rootRoute = () => {
    const root = window.Shopify?.routes?.root || "/";
    return root.endsWith("/") ? root : `${root}/`;
  };

  const formatShekels = (value) => `₪${Number(value).toFixed(2)}`;

  const safeStorage = {
    get(type, key) {
      try {
        return window[type]?.getItem(key) || null;
      } catch {
        return null;
      }
    },
    set(type, key, value) {
      try {
        window[type]?.setItem(key, value);
      } catch {
        // Storage can be unavailable in privacy modes; the UI should still work.
      }
    },
  };

  const parseDisplayedMoney = (value) => {
    const normalized = String(value || "")
      .replace(/[^0-9,.-]/g, "")
      .replace(/,(?=\d{1,2}$)/, ".")
      .replace(/,/g, "");
    const number = Number(normalized);
    return Number.isFinite(number) ? number : 0;
  };

  const isUsableVariantId = (value) => /^\d+$/.test(String(value || "").trim());

  const scratchScrollState = {
    open: new Set(),
    bodyOverflow: "",
    htmlOverflow: "",
  };

  function setScratchScrollLock(overlay, locked) {
    if (locked) {
      if (!scratchScrollState.open.size) {
        scratchScrollState.bodyOverflow = document.body.style.overflow;
        scratchScrollState.htmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
      }
      scratchScrollState.open.add(overlay);
      return;
    }

    scratchScrollState.open.delete(overlay);
    if (!scratchScrollState.open.size) {
      document.body.style.overflow = scratchScrollState.bodyOverflow;
      document.documentElement.style.overflow = scratchScrollState.htmlOverflow;
    }
  }

  function initHeader(scope = document) {
    scope.querySelectorAll("[data-site-header]").forEach((header) => {
      if (header.dataset.initialized) return;
      header.dataset.initialized = "true";

      const menuButton = header.querySelector("[data-menu-trigger]");
      const navigation = header.querySelector("[data-main-navigation]");
      const searchButton = header.querySelector("[data-search-trigger]");
      const searchPanel = header.querySelector("[data-search-panel]");

      menuButton?.addEventListener("click", () => {
        const open = navigation?.classList.toggle("is-open") || false;
        menuButton.setAttribute("aria-expanded", String(open));
        menuButton.setAttribute("aria-label", open ? "סגירת תפריט" : "פתיחת תפריט");
        if (open && searchPanel) {
          searchPanel.hidden = true;
          searchButton?.setAttribute("aria-expanded", "false");
        }
      });

      searchButton?.addEventListener("click", () => {
        const willOpen = searchPanel?.hidden ?? true;
        if (searchPanel) searchPanel.hidden = !willOpen;
        searchButton.setAttribute("aria-expanded", String(willOpen));
        if (willOpen) {
          navigation?.classList.remove("is-open");
          menuButton?.setAttribute("aria-expanded", "false");
          menuButton?.setAttribute("aria-label", "פתיחת תפריט");
          searchPanel?.querySelector("input")?.focus();
        }
      });

      header.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") return;
        const menuWasOpen = navigation?.classList.contains("is-open");
        const searchWasOpen = Boolean(searchPanel && !searchPanel.hidden);
        navigation?.classList.remove("is-open");
        menuButton?.setAttribute("aria-expanded", "false");
        menuButton?.setAttribute("aria-label", "פתיחת תפריט");
        if (searchPanel) searchPanel.hidden = true;
        searchButton?.setAttribute("aria-expanded", "false");
        if (searchWasOpen) searchButton?.focus();
        else if (menuWasOpen) menuButton?.focus();
      });

      navigation?.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", () => {
          navigation.classList.remove("is-open");
          menuButton?.setAttribute("aria-expanded", "false");
          menuButton?.setAttribute("aria-label", "פתיחת תפריט");
        });
      });
    });

    if (!document.documentElement.dataset.headerScrollInitialized) {
      document.documentElement.dataset.headerScrollInitialized = "true";
      let previous = window.scrollY;
      window.addEventListener("scroll", () => {
        const current = window.scrollY;
        document.querySelectorAll("[data-site-header]").forEach((header) => {
          const navigation = header.querySelector("[data-main-navigation]");
          const searchPanel = header.querySelector("[data-search-panel]");
          const hasOpenPanel = navigation?.classList.contains("is-open")
            || Boolean(searchPanel && !searchPanel.hidden);
          if (hasOpenPanel) {
            header.classList.remove("is-hidden");
            return;
          }
          if (current > 125 && current > previous + 2) header.classList.add("is-hidden");
          if (current < previous - 2 || current < 125) header.classList.remove("is-hidden");
        });
        previous = current;
      }, { passive: true });
    }
  }

  function initGalleries(scope = document) {
    scope.querySelectorAll(".product-gallery").forEach((gallery) => {
      if (gallery.dataset.initialized) return;
      gallery.dataset.initialized = "true";
      const mainImage = gallery.querySelector("[data-gallery-main-image], .gallery-main > img");
      const factsPanel = gallery.querySelector("[data-gallery-facts], .gallery-main .supplement-facts");
      const thumbnails = [...gallery.querySelectorAll(".gallery-thumbs button")];
      if (!mainImage || !thumbnails.length) return;
      const productKind = gallery.closest("main")?.querySelector("[data-biolight-product-root]")?.dataset.productKind;
      const galleryAlts = productKind === "supplement"
        ? [
          "בקבוק ואריזת תוסף התמיכה היומית של Biolight",
          "לקוחות מחזיקים בתוסף Biolight",
          "תוסף Biolight ופרטי המותג",
          "פרטי התמיכה היומית של Biolight",
          "אשת מקצוע מצוות Biolight",
          "תוצאות משתמשים של תוסף Biolight",
          "אחריות להחזר כספי על תוסף Biolight",
          "עובדות תזונתיות של תוסף Biolight",
        ]
        : [
          "מכשיר האור האדום Biolight והאריזה הממותגת",
          "לקוחות המשתמשים במכשיר Biolight",
          "מכשיר Biolight ופרטי המותג",
          "יד מחזיקה את מכשיר Biolight",
          "איש מקצוע מצוות Biolight",
          "תוצאות משתמשים של מכשיר Biolight",
          "אחריות להחזר כספי של Biolight",
          "ערכת מכשיר Biolight והאביזרים",
          "מבצע על מכשיר Biolight",
        ];
      thumbnails.forEach((thumbnail, index) => {
        if (!thumbnail.dataset.galleryAlt && galleryAlts[index]) {
          thumbnail.dataset.galleryAlt = galleryAlts[index];
        }
      });
      let active = Math.max(0, thumbnails.findIndex((item) => item.classList.contains("selected")));

      const show = (index) => {
        const next = (index + thumbnails.length) % thumbnails.length;
        const thumbnail = thumbnails[next];
        const image = thumbnail.querySelector("img");
        const showsFacts = Boolean(
          thumbnail.querySelector(".facts-thumb")
          || thumbnail.dataset.galleryTarget === "facts"
          || thumbnail.dataset.galleryType === "facts",
        );

        // Do not leave the gallery in a selected-but-unchanged state if the
        // corresponding facts panel has not been rendered.
        if (showsFacts && !factsPanel) return false;
        if (!showsFacts && !image) return false;

        active = next;
        thumbnails.forEach((button, buttonIndex) => {
          const selected = buttonIndex === active;
          button.classList.toggle("selected", selected);
          button.setAttribute("aria-pressed", String(selected));
        });

        mainImage.hidden = showsFacts;
        if (factsPanel) factsPanel.hidden = !showsFacts;
        if (showsFacts || !image) return true;

        mainImage.src = image.dataset.fullSrc || image.currentSrc || image.src;
        mainImage.removeAttribute("srcset");
        mainImage.alt = thumbnail.dataset.galleryAlt || thumbnail.getAttribute("aria-label") || mainImage.alt;
        return true;
      };

      const move = (direction) => {
        for (let step = 1; step <= thumbnails.length; step += 1) {
          if (show(active + direction * step)) return;
        }
      };

      thumbnails.forEach((button, index) => button.addEventListener("click", () => show(index)));
      gallery.querySelector(".gallery-arrow.prev")?.addEventListener("click", () => move(-1));
      gallery.querySelector(".gallery-arrow.next")?.addEventListener("click", () => move(1));
      show(active);
    });
  }

  function initCountdowns(scope = document) {
    const clocks = [...scope.querySelectorAll(".sale-clock span")];
    if (!clocks.length) return;
    const storageKey = "biolight-shopify-sale-end";
    let state = window.__biolightCountdownState;
    if (!state) {
      let fallbackEnd = Number(safeStorage.get("localStorage", storageKey));
      if (!fallbackEnd || fallbackEnd <= Date.now()) {
        fallbackEnd = Date.now() + 14 * 24 * 60 * 60 * 1000;
        safeStorage.set("localStorage", storageKey, String(fallbackEnd));
      }
      state = { fallbackEnd, timer: null };
      window.__biolightCountdownState = state;
    }

    const endForClock = (clock) => {
      const configuredEnd = clock.dataset.saleEnd || clock.closest("[data-sale-end]")?.dataset.saleEnd || "";
      const fixedEnd = Date.parse(configuredEnd);
      return Number.isFinite(fixedEnd) ? fixedEnd : state.fallbackEnd;
    };

    const textForEnd = (end) => {
      let seconds = Math.max(0, Math.floor((end - Date.now()) / 1000));
      const days = Math.floor(seconds / 86400);
      seconds %= 86400;
      const hours = Math.floor(seconds / 3600);
      seconds %= 3600;
      const minutes = Math.floor(seconds / 60);
      const remainder = seconds % 60;
      return `${days} ימים ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
    };

    const update = (target = document) => {
      target.querySelectorAll(".sale-clock span").forEach((clock) => {
        clock.textContent = textForEnd(endForClock(clock));
      });
    };

    update(scope);
    if (!state.timer) {
      state.timer = window.setInterval(() => update(document), 1000);
      window.__biolightCountdown = state.timer;
    }
  }

  function updateGifts(root, quantity) {
    root.querySelectorAll("[data-gift-needed]").forEach((gift) => {
      const unlocked = quantity >= Number(gift.dataset.giftNeeded);
      const label = gift.dataset.giftLabel || "המתנה";
      gift.classList.toggle("unlocked", unlocked);
      gift.classList.toggle("locked", !unlocked);
      gift.setAttribute("aria-disabled", String(unlocked));
      gift.setAttribute(
        "aria-label",
        unlocked ? `${label} — המתנה נפתחה` : `חשיפת ${label} באמצעות בחירת חבילה גדולה יותר`,
      );
      if (gift.matches("button")) gift.disabled = unlocked;
      const title = gift.querySelector("[data-gift-title]");
      if (title) title.textContent = unlocked ? label : "לחצו לחשיפת המתנה";
    });
    root.querySelectorAll("[data-supplement-gift]").forEach((gift) => {
      const unlocked = quantity >= Number(gift.dataset.needed);
      gift.classList.toggle("unlocked", unlocked);
      gift.classList.toggle("locked", !unlocked);
    });
  }

  function updatePurchaseSummary(root) {
    const selected = root.querySelector("[data-bundle-choice].selected");
    if (!selected) return;
    const baseTotal = parseDisplayedMoney(selected.dataset.bundleTotal);
    if (!baseTotal) return;

    const upsellInput = root.querySelector(".upsell input[type='checkbox']");
    const upsellPrice = parseDisplayedMoney(
      upsellInput?.dataset.upsellPrice
      || root.querySelector(".upsell-price b")?.textContent,
    );
    const total = baseTotal + (upsellInput?.checked && !upsellInput.disabled ? upsellPrice : 0);
    const addButton = root.querySelector("[data-add-to-cart]");
    if (addButton && root.dataset.productKind === "supplement") {
      addButton.textContent = `הוספה לסל — ${formatShekels(total)}`;
    }
    if (addButton) addButton.setAttribute("aria-label", `הוספה לסל, סך הכול ${formatShekels(total)}`);
    const stickyPrice = root.closest("main")?.querySelector(".sticky-product-bar span");
    if (stickyPrice) stickyPrice.textContent = formatShekels(total);
    root.dataset.currentTotal = String(total);
  }

  function syncVariantUI(root) {
    const select = root.querySelector("[data-variant-select]");
    if (!select) {
      const selectedBundle = root.querySelector("[data-bundle-choice].selected");
      if (!selectedBundle) return;
      const isPreview = root.dataset.productPreview === "true";
      const available = !isPreview
        && selectedBundle.dataset.bundleVariantAvailable !== "false";
      const addButton = root.querySelector("[data-add-to-cart]");
      const status = root.querySelector("[data-product-form-status]");
      if (addButton) {
        addButton.disabled = !available;
        addButton.setAttribute("aria-disabled", String(!available));
      }
      if (status) status.textContent = isPreview || available ? "" : "החבילה שבחרתם אינה זמינה כרגע.";
      return;
    }
    const selectedOption = select.selectedOptions?.[0];
    const available = Boolean(selectedOption && !selectedOption.disabled);
    const addButton = root.querySelector("[data-add-to-cart]");
    if (addButton) {
      addButton.disabled = !available;
      addButton.setAttribute("aria-disabled", String(!available));
    }

    const status = root.querySelector("[data-product-form-status]");
    if (status) status.textContent = available ? "" : "האפשרות שבחרתם אינה זמינה כרגע.";
    root.querySelectorAll("[data-selected-variant-price]").forEach((target) => {
      if (selectedOption?.dataset.price) target.textContent = selectedOption.dataset.price;
    });

    const mediaId = selectedOption?.dataset.featuredMediaId;
    if (mediaId) {
      const mediaThumb = root.closest("main")?.querySelector(`.gallery-thumbs [data-media-id="${mediaId}"]`);
      mediaThumb?.click();
    }
  }

  function selectBundle(root, selected) {
    const logicalQuantity = Number(selected.dataset.bundleQuantity || 1);
    const cartQuantity = Number(selected.dataset.cartQuantity || logicalQuantity);
    const bundleVariantMode = root.dataset.bundleVariantMode === "true";
    const bundleVariantId = selected.dataset.bundleVariantId?.trim();
    const isPreview = root.dataset.productPreview === "true";
    const available = !isPreview
      && selected.dataset.bundleVariantAvailable !== "false";
    root.querySelectorAll("[data-bundle-choice]").forEach((choice) => {
      const active = choice === selected;
      choice.classList.toggle("selected", active);
      choice.setAttribute("aria-pressed", String(active));
    });

    if (bundleVariantMode && isUsableVariantId(bundleVariantId)) {
      const variantInput = root.querySelector("[data-variant-id]");
      const variantSelect = root.querySelector("[data-variant-select]");
      if (variantInput) variantInput.value = bundleVariantId;
      if (variantSelect) variantSelect.value = bundleVariantId;
    }

    const quantityInput = root.querySelector("[data-bundle-quantity-input]");
    if (quantityInput) quantityInput.value = String(cartQuantity);
    const bundleProperty = root.querySelector("[data-bundle-property-input]");
    const giftsProperty = root.querySelector("[data-gifts-property-input]");
    if (bundleProperty) bundleProperty.value = selected.dataset.bundleLabel || "";
    if (giftsProperty) giftsProperty.value = selected.dataset.bundleGifts || "";

    const addButton = root.querySelector("[data-add-to-cart]");
    const status = root.querySelector("[data-product-form-status]");
    if (addButton) {
      addButton.disabled = !available;
      addButton.setAttribute("aria-disabled", String(!available));
    }
    if (status) status.textContent = isPreview || available ? "" : "החבילה שבחרתם אינה זמינה כרגע.";

    updateGifts(root, logicalQuantity);
    updatePurchaseSummary(root);
  }

  async function submitProduct(root, form) {
    const submit = form.querySelector("[data-add-to-cart]");
    const status = form.querySelector("[data-product-form-status]");
    const variant = form.querySelector("[data-variant-select]")?.value || form.querySelector("[data-variant-id]")?.value;
    const quantity = Number(form.querySelector("[data-bundle-quantity-input]")?.value || 1);
    const selectedBundle = root.querySelector("[data-bundle-choice].selected");
    if (!isUsableVariantId(variant)) throw new Error("לא נמצא וריאנט זמין למוצר.");
    if (!Number.isInteger(quantity) || quantity < 1) throw new Error("הכמות שנבחרה אינה תקינה.");
    if (selectedBundle?.dataset.bundleVariantAvailable === "false") {
      throw new Error("החבילה שבחרתם אינה זמינה כרגע.");
    }

    const properties = {};
    form.querySelectorAll("[data-bundle-property-input], [data-gifts-property-input]").forEach((input) => {
      const match = input.name?.match(/^properties\[(.+)]$/);
      if (match && input.value) properties[match[1]] = input.value;
    });
    const mainItem = { id: variant, quantity };
    if (Object.keys(properties).length) mainItem.properties = properties;
    const items = [mainItem];
    const upsellChecked = root.querySelector(".upsell input[type='checkbox']")?.checked;
    const upsellId = root.dataset.upsellVariantId?.trim();
    if (upsellChecked && !isUsableVariantId(upsellId)) {
      throw new Error("התוספת אינה מוגדרת כרגע. בטלו את הסימון ונסו שוב.");
    }
    if (upsellChecked) items.push({ id: upsellId, quantity: 1 });

    if (submit) submit.disabled = true;
    if (status) status.textContent = "מוסיף לסל…";
    const response = await fetch(`${rootRoute()}cart/add.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || data.message || "לא ניתן היה להוסיף את המוצר לסל.");
    }
    if (window.BiolightCartDrawer) {
      window.BiolightCartDrawer.openAndRefresh();
      if (submit) submit.disabled = false;
      if (status) status.textContent = "";
    } else {
      window.location.assign(`${rootRoute()}cart`);
    }
  }

  function syncPublishedStorefront(scope = document) {
    const productRoots = [];
    if (scope.matches?.("[data-biolight-product-root]")) productRoots.push(scope);
    scope.querySelectorAll?.("[data-biolight-product-root]").forEach((root) => productRoots.push(root));

    productRoots.forEach((root) => {
      const title = root.closest(".product-info")?.querySelector("h1");
      if (title) title.textContent = "Biolight — מכשיר אור אדום להקלה באף";

      if (root.dataset.productPreview !== "true") return;
      root.querySelectorAll(".shopify-variant-picker, .dynamic-checkout").forEach((element) => {
        element.hidden = true;
      });
      root.querySelectorAll("[data-bundle-choice]").forEach((choice) => {
        choice.dataset.bundleVariantAvailable = "true";
      });
      const submit = root.querySelector("[data-add-to-cart]");
      if (submit) {
        submit.classList.remove("preview-visual-enabled");
        submit.disabled = true;
        submit.setAttribute("aria-disabled", "true");
        submit.textContent = "הרכישה תיפתח לאחר חיבור המוצר";
      }
      const previewNotice = root.querySelector(".product-preview-notice");
      if (previewNotice) {
        previewNotice.hidden = false;
        previewNotice.textContent = "עמוד המוצר מוצג במצב תצוגה. כדי להפעיל הזמנות, יש לחבר לתבנית מוצר Shopify פעיל עם וריאנטים זמינים.";
      }
    });

    scope.querySelectorAll?.(".customer-widget").forEach((widget) => {
      if (!widget.querySelector(".review-bars") || widget.dataset.sourceSynced === "true") return;
      widget.dataset.sourceSynced = "true";

      const head = widget.querySelector(".customer-widget-head");
      if (head) {
        head.replaceChildren();
        const heading = document.createElement("h2");
        heading.textContent = "פרופילי שימוש לדוגמה";
        const disclosure = document.createElement("p");
        disclosure.textContent = "התמונות, השמות והסיפורים בחלק זה נועדו להמחשה בלבד";
        head.append(heading, disclosure);
      }
      widget.querySelector(".review-bars")?.remove();
      const subheading = widget.querySelector(":scope > h3");
      if (subheading) subheading.textContent = "סיפורי שימוש להמחשה";

      const profiles = [
        ["רותי · חיפה", "בת 62, משלבת את המכשיר בשגרת הערב הביתית שלה כדי להקדיש עשר דקות שקטות לנוחות הנשימה."],
        ["אברהם · באר שבע", "בן 58, מעדיף פתרון נטען ונוח שאפשר לקחת בתיק ולהשתמש בו בבית בהתאם להוראות."],
        ["מיכל · רמת גן", "בת 46, אוהבת שההפעלה פשוטה ושמדריך השימוש מסביר בבירור כל שלב."],
        ["יוסי · ראשון לציון", "בן 54, מקפיד להכין את המכשיר מראש כחלק משגרת הערב הקבועה שלו."],
      ];
      widget.querySelectorAll(".customer-photo-grid article").forEach((card, index) => {
        const profile = profiles[index];
        if (!profile) return;
        card.querySelectorAll(".trust-stars, h4, .demo-profile-label").forEach((element) => element.remove());
        let name = card.querySelector(":scope > strong");
        if (!name) {
          name = document.createElement("strong");
          const photo = card.querySelector(".customer-photo");
          photo?.insertAdjacentElement("afterend", name);
        }
        name.textContent = profile[0];
        const story = card.querySelector(":scope > p");
        if (story) story.textContent = profile[1];
      });
    });
  }

  function initProducts(scope = document) {
    scope.querySelectorAll("[data-biolight-product-root]").forEach((root) => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = "true";
      const form = root.querySelector("form.shopify-product-form");
      if (!form) return;

      root.querySelectorAll("[data-bundle-choice]").forEach((choice) => {
        const unavailable = choice.dataset.bundleVariantAvailable === "false";
        choice.disabled = unavailable;
        choice.setAttribute("aria-disabled", String(unavailable));
        choice.addEventListener("click", () => selectBundle(root, choice));
      });
      root.querySelectorAll("[data-gift-needed]").forEach((gift) => {
        gift.addEventListener("click", () => {
          if (!gift.classList.contains("locked")) return;
          const needed = gift.dataset.giftNeeded;
          const matching = root.querySelector(`[data-bundle-choice][data-bundle-quantity="${needed}"]`);
          if (matching && !matching.disabled) selectBundle(root, matching);
        });
      });

      const upsell = root.querySelector(".upsell");
      const upsellInput = upsell?.querySelector("input[type='checkbox']");
      const upsellId = root.dataset.upsellVariantId?.trim();
      const dynamicCheckout = root.querySelector(".dynamic-checkout");
      if (upsell && upsellInput && !isUsableVariantId(upsellId)) {
        upsellInput.checked = false;
        upsellInput.disabled = true;
        upsell.hidden = true;
      } else if (upsellInput) {
        if (dynamicCheckout) dynamicCheckout.hidden = upsellInput.checked;
        upsellInput.addEventListener("change", () => {
          if (dynamicCheckout) dynamicCheckout.hidden = upsellInput.checked;
          updatePurchaseSummary(root);
        });
      }

      const variantSelect = root.querySelector("[data-variant-select]");
      variantSelect?.addEventListener("change", () => syncVariantUI(root));

      const initialSelected = root.querySelector("[data-bundle-choice].selected");
      const initial = (initialSelected && !initialSelected.disabled ? initialSelected : null)
        || root.querySelector("[data-bundle-choice]:not([disabled])");
      if (initial) selectBundle(root, initial);
      syncVariantUI(root);

      form.addEventListener("submit", async (event) => {
        if (event.submitter?.classList.contains("shopify-payment-button__button")) return;
        event.preventDefault();
        if (root.dataset.productPreview === "true") {
          const status = form.querySelector("[data-product-form-status]");
          if (status) status.textContent = "";
          return;
        }
        try {
          await submitProduct(root, form);
        } catch (error) {
          const status = form.querySelector("[data-product-form-status]");
          if (status) status.textContent = error instanceof Error ? error.message : "אירעה שגיאה. נסו שוב.";
          const submit = form.querySelector("[data-add-to-cart]");
          const variantSelect = form.querySelector("[data-variant-select]");
          const unavailable = root.dataset.productPreview === "true"
            || Boolean(variantSelect?.selectedOptions?.[0]?.disabled);
          if (submit) {
            submit.disabled = unavailable;
            submit.setAttribute("aria-disabled", String(unavailable));
          }
        }
      });
    });
  }

  function initScratchCards(scope = document) {
    if (window.Shopify?.designMode) return;
    scope.querySelectorAll("[data-scratch-overlay]").forEach((overlay) => {
      if (overlay.dataset.initialized) return;
      overlay.dataset.initialized = "true";
      overlay.setAttribute("aria-hidden", "true");
      if (safeStorage.get("sessionStorage", "biolight-scratch-seen")) return;

      const canvas = overlay.querySelector("[data-scratch-canvas]");
      const card = overlay.querySelector("[data-scratch-card]");
      const revealButton = overlay.querySelector("[data-scratch-reveal]");
      const claim = overlay.querySelector("[data-scratch-claim]");
      const closeButton = overlay.querySelector("[data-scratch-close]");
      const gate = overlay.querySelector("[data-scratch-gate]");
      const gateForm = overlay.querySelector("[data-scratch-gate-form]");
      const emailInput = overlay.querySelector("[data-scratch-email]");
      const gateError = overlay.querySelector("[data-scratch-error]");
      const invite = overlay.querySelector("[data-scratch-invite]");
      const won = overlay.querySelector("[data-scratch-won]");
      let drawing = false;
      let strokes = 0;
      let previousFocus = null;

      const reveal = () => {
        card?.classList.add("is-revealed");
        if (canvas) canvas.hidden = true;
        if (revealButton) revealButton.hidden = true;
        safeStorage.set("sessionStorage", "biolight-scratch-seen", "1");
        if (gate) {
          gate.hidden = false;
          emailInput?.focus();
          return;
        }
        if (claim) claim.hidden = false;
        claim?.focus();
      };

      const showGateError = (message) => {
        if (!gateError) return;
        gateError.textContent = message;
        gateError.hidden = false;
      };

      const unlockCode = () => {
        if (gate) gate.hidden = true;
        if (invite) invite.hidden = true;
        if (won) won.hidden = false;
        if (claim) claim.hidden = false;
        claim?.focus();
      };

      gateForm?.addEventListener("submit", (event) => {
        event.preventDefault();
        const email = emailInput?.value.trim() || "";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          showGateError("נא להזין כתובת אימייל תקינה.");
          emailInput?.focus();
          return;
        }
        if (gateError) gateError.hidden = true;
        // Best-effort signup; the reward is shown regardless so the store's
        // spam protection can never block the customer from getting the code.
        fetch(gateForm.action, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams(new FormData(gateForm)),
        }).catch(() => {});
        unlockCode();
      });

      const close = () => {
        if (overlay.hidden) return;
        overlay.hidden = true;
        overlay.setAttribute("aria-hidden", "true");
        drawing = false;
        safeStorage.set("sessionStorage", "biolight-scratch-seen", "1");
        setScratchScrollLock(overlay, false);
        if (previousFocus?.isConnected && typeof previousFocus.focus === "function") previousFocus.focus();
      };

      const drawCover = () => {
        if (!canvas) return;
        const context = canvas.getContext("2d");
        if (!context) return;
        const ratio = Math.max(1, window.devicePixelRatio || 1);
        canvas.width = 600 * ratio;
        canvas.height = 390 * ratio;
        context.scale(ratio, ratio);
        context.fillStyle = "#247976";
        context.fillRect(0, 0, 600, 390);
        context.fillStyle = "rgba(255,255,255,.18)";
        for (let index = 0; index < 25; index += 1) {
          context.beginPath();
          context.arc((index * 97) % 610, (index * 53) % 400, 8 + (index % 5) * 4, 0, Math.PI * 2);
          context.fill();
        }
        context.fillStyle = "#fff";
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.font = "700 38px Rubik, Arial";
        context.fillText("גרדו כאן", 300, 178);
        context.font = "700 19px Rubik, Arial";
        context.fillText("כדי לחשוף את ההטבה", 300, 224);
      };

      const scratch = (event) => {
        if (!drawing || !canvas || canvas.hidden) return;
        const context = canvas.getContext("2d");
        if (!context) return;
        const rect = canvas.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 600;
        const y = ((event.clientY - rect.top) / rect.height) * 390;
        context.save();
        context.globalCompositeOperation = "destination-out";
        context.beginPath();
        context.arc(x, y, 34, 0, Math.PI * 2);
        context.fill();
        context.restore();
        strokes += 1;
        if (strokes > 44) reveal();
      };

      canvas?.addEventListener("pointerdown", (event) => {
        drawing = true;
        canvas.setPointerCapture(event.pointerId);
        scratch(event);
      });
      canvas?.addEventListener("pointermove", scratch);
      canvas?.addEventListener("pointerup", () => { drawing = false; });
      canvas?.addEventListener("pointercancel", () => { drawing = false; });
      closeButton?.addEventListener("click", close);
      revealButton?.addEventListener("click", reveal);
      claim?.addEventListener("click", close);
      overlay.addEventListener("click", (event) => { if (event.target === overlay) close(); });
      overlay.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== "Tab") return;
        const focusable = [...overlay.querySelectorAll(
          "a[href]:not([hidden]), button:not([disabled]):not([hidden]), input:not([type='hidden']):not([disabled]), [tabindex]:not([tabindex='-1']):not([hidden])",
        )].filter((element) => element.getClientRects().length > 0);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      window.setTimeout(() => {
        if (!overlay.isConnected || safeStorage.get("sessionStorage", "biolight-scratch-seen")) return;
        previousFocus = document.activeElement;
        overlay.hidden = false;
        overlay.setAttribute("aria-hidden", "false");
        setScratchScrollLock(overlay, true);
        drawCover();
        window.requestAnimationFrame(() => closeButton?.focus());
      }, 2800);
    });
  }

  function initBackToTop(scope = document) {
    scope.querySelectorAll(".back-to-top").forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = "true";
      const update = () => button.classList.toggle("is-visible", window.scrollY > 700);
      window.addEventListener("scroll", update, { passive: true });
      button.addEventListener("click", () => window.scrollTo({ top: 0, behavior: "smooth" }));
      update();
    });
  }

  function initialize(scope = document) {
    [
      syncPublishedStorefront,
      initHeader,
      initGalleries,
      initCountdowns,
      initProducts,
      initScratchCards,
      initBackToTop,
    ].forEach((initializer) => {
      try {
        initializer(scope);
      } catch (error) {
        console.error(`[Biolight] ${initializer.name} failed`, error);
      }
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initialize());
  else initialize();

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
